/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * VPS Skills Library Service
 * 
 * API client for VPS Skills Library endpoints.
 * Uses shared response types and handleApiResponse for consistent error handling.
 * 
 * For simple GET endpoints, components can use createQueryFn directly:
 *   import { createQueryFn } from '@shared/lib/api';
 *   const { data } = useQuery({
 *     queryKey: SKILLS_QUERY_KEYS.index,
 *     queryFn: createQueryFn<SkillIndexResponseData>(SKILLS_ENDPOINTS.index),
 *   });
 */

import { authenticatedFetch, createQueryFn, createMutationFn } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import type {
  SkillIndexEntry,
  SkillReadResult,
} from '@shared/schema/skills';
import type {
  ListSkillsResponseData,
  LoadSkillsResponseData,
  SearchSkillsResponseData,
  SkillIndexResponseData,
  ConnectorSkillInfo,
  ListConnectorSkillsResponseData,
} from '@shared/types/responses/skills';

// ============================================================================
// API Endpoints - Centralized endpoint definitions
// ============================================================================

export const SKILLS_ENDPOINTS = {
  list: '/api/vps-skills',
  index: '/api/vps-skills/index',
  indexRebuild: '/api/vps-skills/index/rebuild',
  stats: '/api/vps-skills/stats',
  search: '/api/vps-skills/search',
  load: '/api/vps-skills/load',
  connectors: '/api/vps-skills/connectors',
  read: (id: string) => `/api/vps-skills/${id}`,
  files: (id: string) => `/api/vps-skills/${id}/files`,
  file: (id: string, filename: string) => `/api/vps-skills/${id}/files/${filename}`,
} as const;

// ============================================================================
// Query Keys - For React Query cache management
// ============================================================================

export const SKILLS_QUERY_KEYS = {
  all: ['vps-skills'] as const,
  list: ['vps-skills', 'list'] as const,
  index: ['vps-skills', 'index'] as const,
  stats: ['vps-skills', 'stats'] as const,
  connectors: ['vps-skills', 'connectors'] as const,
  detail: (id: string) => ['vps-skills', 'detail', id] as const,
  files: (id: string) => ['vps-skills', 'files', id] as const,
  search: (query: string) => ['vps-skills', 'search', query] as const,
} as const;

// Re-export types for convenience
export type { SkillIndexEntry as SkillEntry };
export type {
  LoadSkillsResponseData as LoadSkillsResponse,
  SearchSkillsResponseData as SearchSkillsResponse,
  SkillIndexResponseData as SkillIndexResponse,
  ConnectorSkillInfo,
};

export interface SkillStatsResponse {
  index: {
    skillsCount: number;
    indexAge: number | null;
    indexExists: boolean;
  };
}

// ============================================================================
// Query/Mutation Function Factories - For direct use in components
// ============================================================================

import type { SkillIndex } from '@shared/schema/skills';

/**
 * Query function for skills index - use with useQuery
 * 
 * Transforms the API response from SkillIndex format (with organizational/products/connectors)
 * to SkillIndexResponseData format (with flat entries array) for UI consumption.
 */
export const skillsIndexQueryFn = async (): Promise<SkillIndexResponseData> => {
  const response = await authenticatedFetch(SKILLS_ENDPOINTS.index);
  const index = await handleApiResponse<SkillIndex>(response);
  
  // Flatten organizational, products, and connectors into a single entries array
  const entries: SkillIndexEntry[] = [];
  const byCategory: Record<string, number> = {};
  let connectorCount = 0;
  let proceduralCount = 0;
  
  // Add organizational skills
  if (index.organizational) {
    for (const category of Object.keys(index.organizational)) {
      const categorySkills = index.organizational[category as keyof typeof index.organizational];
      if (categorySkills) {
        entries.push(...categorySkills);
        byCategory[category] = (byCategory[category] || 0) + categorySkills.length;
        for (const skill of categorySkills) {
          if (skill.type === 'connector') {
            connectorCount++;
          } else {
            proceduralCount++;
          }
        }
      }
    }
  }
  
  // Add product skills
  if (index.products) {
    for (const productId of Object.keys(index.products)) {
      const product = index.products[productId];
      if (product?.skills) {
        entries.push(...product.skills);
        for (const skill of product.skills) {
          if (skill.category) {
            byCategory[skill.category] = (byCategory[skill.category] || 0) + 1;
          }
          if (skill.type === 'connector') {
            connectorCount++;
          } else {
            proceduralCount++;
          }
        }
      }
    }
  }
  
  // Add connector skills (if not already in organizational/products)
  if (index.connectors) {
    for (const connector of index.connectors) {
      // Check if not already added
      if (!entries.some(e => e.path === connector.path)) {
        entries.push(connector);
        if (connector.category) {
          byCategory[connector.category] = (byCategory[connector.category] || 0) + 1;
        }
        connectorCount++;
      }
    }
  }
  
  return {
    entries,
    stats: {
      totalSkills: entries.length,
      byCategory,
      connectorSkills: connectorCount,
      proceduralSkills: proceduralCount,
      totalTokens: 0, // Skills don't track tokens in the index
    },
    generatedAt: index.lastUpdated,
  };
};

/** Query function for skills stats - use with useQuery */
export const skillsStatsQueryFn = createQueryFn<SkillStatsResponse>(SKILLS_ENDPOINTS.stats);

/** Mutation function for rebuilding skills index - use with useMutation */
export const rebuildSkillsIndexMutationFn = createMutationFn<SkillIndexResponseData, void>(SKILLS_ENDPOINTS.indexRebuild, 'POST');

// ============================================================================
// Service Functions - For complex operations with transformations
// ============================================================================

/**
 * List all loaded skills
 */
export async function listSkills(): Promise<SkillIndexEntry[]> {
  const response = await authenticatedFetch('/api/vps-skills');
  const data = await handleApiResponse<ListSkillsResponseData>(response);
  return data.skills;
}

/**
 * Load skills from a directory
 */
export async function loadSkills(
  velocitySkillsPath?: string
): Promise<LoadSkillsResponseData> {
  const response = await authenticatedFetch('/api/vps-skills/load', {
    method: 'POST',
    body: { velocitySkillsPath },
  });
  return handleApiResponse<LoadSkillsResponseData>(response);
}

/**
 * Search skills
 */
export async function searchSkills(
  query?: string,
  options?: {
    tags?: string[];
    category?: string;
    type?: 'procedural' | 'connector';
    scope?: 'all' | 'organizational' | 'product';
    limit?: number;
  }
): Promise<SearchSkillsResponseData> {
  const body: Record<string, any> = { limit: options?.limit ?? 10 };
  if (query) body.query = query;
  if (options?.tags?.length) body.tags = options.tags;
  if (options?.category) body.category = options.category;
  if (options?.type) body.type = options.type;
  if (options?.scope && options.scope !== 'all') body.scope = options.scope;

  const response = await authenticatedFetch('/api/vps-skills/search', {
    method: 'POST',
    body,
  });
  return handleApiResponse<SearchSkillsResponseData>(response);
}

/**
 * Read a skill's SKILL.md content
 */
export async function readSkill(skillPath: string): Promise<SkillReadResult> {
  const response = await authenticatedFetch(`/api/vps-skills/${encodeURIComponent(skillPath)}`);
  return handleApiResponse<SkillReadResult>(response);
}

/**
 * List files in a skill directory
 */
export async function listSkillFiles(skillPath: string): Promise<string[]> {
  const response = await authenticatedFetch(`/api/vps-skills/${encodeURIComponent(skillPath)}/files`);
  const data = await handleApiResponse<{ files: string[] }>(response);
  return data.files;
}

/**
 * Read a specific file from a skill
 */
export async function readSkillFile(skillPath: string, fileName: string): Promise<string> {
  const response = await authenticatedFetch(
    `/api/vps-skills/${encodeURIComponent(skillPath)}/files/${encodeURIComponent(fileName)}`
  );
  const data = await handleApiResponse<{ content: string }>(response);
  return data.content;
}

/**
 * Get the skills index
 */
export async function getSkillsIndex(): Promise<SkillIndexResponseData> {
  const response = await authenticatedFetch('/api/vps-skills/index');
  return handleApiResponse<SkillIndexResponseData>(response);
}

/**
 * Rebuild the skills index
 */
export async function rebuildSkillsIndex(
  velocitySkillsPath?: string
): Promise<SkillIndexResponseData> {
  const response = await authenticatedFetch('/api/vps-skills/index/rebuild', {
    method: 'POST',
    body: { velocitySkillsPath },
  });
  return handleApiResponse<SkillIndexResponseData>(response);
}

/**
 * List connector skills
 */
export async function listConnectorSkills(): Promise<ConnectorSkillInfo[]> {
  const response = await authenticatedFetch('/api/vps-skills/connectors');
  const data = await handleApiResponse<ListConnectorSkillsResponseData>(response);
  return data.connectors;
}

/**
 * Get skills statistics
 */
export async function getSkillsStats(): Promise<SkillStatsResponse> {
  const response = await authenticatedFetch('/api/vps-skills/stats');
  return handleApiResponse<SkillStatsResponse>(response);
}
